/**
 * Marketing attribution — which ad, campaign or outside site brought this visitor.
 *
 * Loaded on every page from base.html. For the life of the tab it keeps two
 * touches in sessionStorage:
 *
 *   first — the first page of the session, whatever brought the visitor;
 *   last  — the latest arrival that carried a campaign signal: UTM tags, an ad
 *           click id (gclid / fbclid / msclkid …) or a referrer from another site.
 *           A later ad click replaces it; browsing within the site never does.
 *
 * Forms send it as `marketing_attribution`:
 *
 *   - native POST forms get a hidden input filled in on submit (below), so a
 *     server-rendered form needs no change;
 *   - forms that post JSON with fetch add `marketing_attribution:
 *     window.getMarketingAttribution()` to their body.
 *
 * The server cleans and classifies it — common/services/marketing_attribution.py.
 * Only the landing *path* is kept, never its query string, so nothing a URL
 * happens to carry beyond the tracked parameters is stored.
 */
(function () {
    'use strict';

    var STORAGE_KEY = 'shining.marketing_attribution.v1';
    var FIELD = 'marketing_attribution';
    var PARAMS = [
        'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
        'gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id'
    ];

    function read() {
        try {
            var raw = window.sessionStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    }

    function write(state) {
        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) { /* private mode or storage full: the in-page copy still serves this page */ }
    }

    function externalReferrer() {
        var ref = document.referrer || '';
        if (!ref) return '';
        try {
            if (new URL(ref).hostname === window.location.hostname) return '';
        } catch (e) {
            return '';
        }
        return ref.slice(0, 2000);
    }

    function currentTouch() {
        var touch = {
            landing_page: window.location.pathname,
            landed_at: new Date().toISOString()
        };
        var signal = false;
        var params = null;
        try { params = new URLSearchParams(window.location.search); } catch (e) { params = null; }
        if (params) {
            PARAMS.forEach(function (key) {
                var value = params.get(key);
                if (value) {
                    touch[key] = value.slice(0, 255);
                    signal = true;
                }
            });
        }
        var referrer = externalReferrer();
        if (referrer) {
            touch.referrer = referrer;
            signal = true;
        }
        return { touch: touch, signal: signal };
    }

    var state = read() || {};
    var arrival = currentTouch();
    if (!state.first) state.first = arrival.touch;
    if (arrival.signal) state.last = arrival.touch;
    write(state);

    window.getMarketingAttribution = function () {
        return read() || state;
    };

    // Capture phase, so the field is in place before any page handler runs — the
    // contact form cancels the event and calls form.submit() later, which does not
    // fire another submit event.
    document.addEventListener('submit', function (event) {
        var form = event.target;
        if (!form || form.tagName !== 'FORM') return;
        if ((form.getAttribute('method') || 'get').toLowerCase() !== 'post') return;
        if (form.hasAttribute('data-no-attribution')) return;

        var input = form.querySelector('input[name="' + FIELD + '"]');
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = FIELD;
            form.appendChild(input);
        }
        try {
            input.value = JSON.stringify(window.getMarketingAttribution() || {});
        } catch (e) { /* never block a submission */ }
    }, true);
})();
